import { API_URL } from "@/lib/api";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { MainLayout } from "@/components/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, Loader2, Play, Mic, RefreshCw, Square, Trash2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import {
  authAPI,
  alunosAPI,
  cardsAPI,
  respostasAPI,
  categoriasAPI,
  sessaoVencida,
  Card as CardType,
  Categoria,
  Dificuldade,
} from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardTextDisplay } from "@/components/card";
import { isStudentPreview } from "@/lib/studentPreview";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * As quatro avaliações, na ordem dos botões. A tecla é o atalho no computador.
 */
const AVALIACOES: { valor: Dificuldade; rotulo: string; emoji: string; classe: string; tecla: string }[] = [
  { valor: "facil", rotulo: "Fácil", emoji: "🟢", classe: "bg-facil hover:bg-facil/90", tecla: "1" },
  { valor: "medio", rotulo: "Médio", emoji: "🟡", classe: "bg-medio hover:bg-medio/90", tecla: "2" },
  { valor: "dificil", rotulo: "Difícil", emoji: "🟠", classe: "bg-dificil hover:bg-dificil/90", tecla: "3" },
  { valor: "impossivel", rotulo: "Impossível", emoji: "🔴", classe: "bg-impossivel hover:bg-impossivel/90", tecla: "4" },
];

type Resumo = Record<Dificuldade, number>;
const RESUMO_VAZIO: Resumo = { facil: 0, medio: 0, dificil: 0, impossivel: 0 };

/** Áudio e imagem dos cards vêm com o caminho relativo à API. */
const urlDaMidia = (caminho?: string | null): string | null => {
  if (!caminho) return null;
  return caminho.startsWith("/") ? `${API_URL}${caminho}` : caminho;
};

/** A preferência "tocar o áudio sozinho", guardada no aparelho. */
const CHAVE_AUDIO_AUTOMATICO = "memoire:audio-automatico";
const lerAudioAutomatico = () => {
  try {
    return localStorage.getItem(CHAVE_AUDIO_AUTOMATICO) === "1";
  } catch {
    return false;
  }
};

const AlunoCards = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Modo de estudo por categoria. Dois formatos aceitos:
  //  - Legado, uma categoria:  /aluno/cards?categoria_id=1&categoria_nome=Vocabulário
  //  - Sequência de várias:     /aluno/cards?categoria_ids=1,2,3
  // Ambos viram uma LISTA ordenada. Sem param de categoria → estudo por módulo.
  const categoriaIdParam = searchParams.get("categoria_id");
  const categoriaIdsParam = searchParams.get("categoria_ids");
  const categoriaNomeParam = searchParams.get("categoria_nome") || "";
  const moduloParam = searchParams.get("modulo") || "";
  const sequenceIds = categoriaIdsParam
    ? categoriaIdsParam.split(",").map(Number).filter(Boolean)
    : categoriaIdParam
      ? [Number(categoriaIdParam)]
      : [];
  const estudandoPorCategoria = sequenceIds.length > 0;

  // Índice da categoria atual dentro da sequência
  const [seqIndex, setSeqIndex] = useState(0);
  const currentCategoriaId = sequenceIds[seqIndex];
  // Metadados (nome/cor) das categorias, para rótulos e o modal de pausa
  const [categoriasMeta, setCategoriasMeta] = useState<Categoria[]>([]);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);

  const nomeCategoria = (id?: number) =>
    categoriasMeta.find((c) => c.id === id)?.nome || categoriaNomeParam || "selecionada";
  const currentCategoriaNome = nomeCategoria(currentCategoriaId);
  const nextCategoriaNome = nomeCategoria(sequenceIds[seqIndex + 1]);
  const currentUser = authAPI.getCurrentUser();
  const previewMode = isStudentPreview();
  const [user, setUser] = useState({
    nome: currentUser?.nome || "Aluno",
    foto: currentUser?.foto || "",
    sexo: "Prefiro não dizer" as "Masculino" | "Feminino" | "Prefiro não dizer",
  });

  const [cards, setCards] = useState<CardType[]>([]);
  const [originalCount, setOriginalCount] = useState(0);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  // Falha ao carregar NÃO é "revisões em dia": sem isto, o aluno sem internet
  // via a tela de parabéns e ia embora achando que não tinha o que estudar.
  const [erroDeCarga, setErroDeCarga] = useState<"rede" | "sessao" | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [progressionStep, setProgressionStep] = useState<"welcome" | "ask_video" | "confirm_without_video">("welcome");

  const [isProgressionModalOpen, setIsProgressionModalOpen] = useState(false);
  const [nextModuloToUnlock, setNextModuloToUnlock] = useState<any>(null);
  const [alunoIdToUpdate, setAlunoIdToUpdate] = useState<number | null>(null);

  // A sessão inteira (todas as categorias de uma sequência): o que o aluno
  // respondeu, e quando cada card volta — para a tela de encerramento.
  const [resumo, setResumo] = useState<Resumo>(RESUMO_VAZIO);
  const [fim, setFim] = useState(false);
  // card → em quantos dias ele volta (a resposta mais recente de cada um).
  const proximasRevisoes = useRef(new Map<number, number>());
  const inicioDaSessao = useRef(Date.now());
  const inicioDoCard = useRef(Date.now());

  // Uma avaliação por vez. A trava é um ref, e não só o estado: dois toques
  // seguidos chegam antes de o React redesenhar o botão desabilitado, e cada
  // um gravava uma resposta — o card "fácil" pulava de 1 para 6 dias.
  const [salvando, setSalvando] = useState<Dificuldade | null>(null);
  const travaDeResposta = useRef(false);

  // Carrega nomes/cores das categorias uma vez (para rótulos e modal de pausa)
  useEffect(() => {
    categoriasAPI
      .getAll()
      .then((data) => setCategoriasMeta(data.categorias || []))
      .catch((error) => console.error("Erro ao carregar categorias:", error));
  }, []);

  // Recarrega (e reinicia a sessão) quando a categoria atual muda — seja por
  // troca de URL, seja por avanço na sequência (setSeqIndex → currentCategoriaId).
  useEffect(() => {
    if (!currentUser) {
      navigate("/");
      return;
    }
    loadData();
  }, [currentCategoriaId]);

  const loadData = async () => {
    setLoading(true);
    setErroDeCarga(null);
    setCards([]);
    setCurrentCardIndex(0);
    setCurrentStep(1);
    setFim(false);
    try {
      // 1. Obter dados do perfil do aluno para saber o módulo inicial
      let moduloInicial = "";
      let moduloInicialCategoriaId: number | undefined;

      if (!previewMode) {
        const alunoData = await alunosAPI.getByUsuarioId(currentUser.id);
        if (alunoData.has_profile && alunoData.aluno) {
        setUser({
          nome: currentUser.nome,
          foto: currentUser.foto || "",
          sexo: (alunoData.aluno.sexo as any) || "Prefiro não dizer",
        });
        moduloInicial = alunoData.aluno.modulo_inicial_nome || "";
        moduloInicialCategoriaId = alunoData.aluno.modulo_inicial_categoria_id ?? undefined;
        }
      }

      // 2. Buscar os cards: por CATEGORIA (se veio na URL) ou pelo módulo do aluno
      // Importante: passar categoria_id junto com modulo evita misturar cards
      // de categorias diferentes que tenham módulo com o mesmo nome (ex: "Módulo 1")
      const cardsResponse = estudandoPorCategoria
        ? await cardsAPI.getAll({ categoria_id: currentCategoriaId, ...(moduloParam ? { modulo: moduloParam } : {}) })
        : moduloInicial
          ? await cardsAPI.getAll({ modulo: moduloInicial, categoria_id: moduloInicialCategoriaId })
          : await cardsAPI.getAll();
      const fetchedCards = cardsResponse.cards || [];
      setCards(fetchedCards);
      setOriginalCount(fetchedCards.length);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setErroDeCarga(sessaoVencida(error) ? "sessao" : "rede");
    } finally {
      setLoading(false);
    }
  };

  const currentCard = cards[currentCardIndex];

  /* ------------------------------------------------------------- áudio */

  // Um áudio por vez. Cada toque em "Ouvir" criava um `new Audio()` solto:
  // dois toques tocavam a frase em cima dela mesma, e o áudio do card anterior
  // continuava falando depois de o aluno avançar.
  const audioAtual = useRef<HTMLAudioElement | null>(null);
  const [tocando, setTocando] = useState(false);
  const [audioAutomatico, setAudioAutomatico] = useState(lerAudioAutomatico);

  const pararAudio = useCallback(() => {
    audioAtual.current?.pause();
    audioAtual.current = null;
    setTocando(false);
  }, []);

  const tocar = useCallback(
    (url: string, aoFalhar?: () => void) => {
      pararAudio();
      const audio = new Audio(url);
      audioAtual.current = audio;
      setTocando(true);
      const encerrar = () => {
        if (audioAtual.current === audio) {
          audioAtual.current = null;
          setTocando(false);
        }
      };
      audio.onended = encerrar;
      audio.onerror = () => {
        const eraEste = audioAtual.current === audio;
        encerrar();
        if (eraEste) aoFalhar?.();
      };
      // O navegador pode recusar (sem toque do usuário, aba em segundo plano).
      audio.play().catch(() => {
        const eraEste = audioAtual.current === audio;
        encerrar();
        if (eraEste) aoFalhar?.();
      });
    },
    [pararAudio],
  );

  const urlDoAudio = urlDaMidia(currentCard?.audio_path);

  const playNativeAudio = () => {
    if (!urlDoAudio) return;
    if (tocando) {
      pararAudio();
      return;
    }
    tocar(urlDoAudio, () => toast.error("Não foi possível tocar o áudio deste card."));
  };

  /* ---------------------------------------------------------- gravação */

  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [userAudioUrl, setUserAudioUrl] = useState<string | null>(null);
  const microfone = useRef<MediaStream | null>(null);
  // O gravador em uso. Trocar de card no meio de uma gravação a descarta: o
  // `onstop` confere se ainda é ele antes de guardar e tocar.
  const gravadorAtivo = useRef<MediaRecorder | null>(null);
  const podeGravar =
    typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined";

  const soltarMicrofone = () => {
    microfone.current?.getTracks().forEach((track) => track.stop());
    microfone.current = null;
  };

  const startRecording = async () => {
    try {
      pararAudio();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microfone.current = stream;
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        soltarMicrofone();
        // Gravação cancelada pela troca de card: não toca nem guarda.
        if (recorder !== gravadorAtivo.current) return;
        gravadorAtivo.current = null;
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        const url = URL.createObjectURL(blob);
        setUserAudioUrl((antiga) => {
          if (antiga) URL.revokeObjectURL(antiga);
          return url;
        });
        tocar(url);
        toast.success("Gravação concluída. Compare com a pronúncia do card.");
      };

      gravadorAtivo.current = recorder;
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error("Erro ao acessar microfone:", err);
      soltarMicrofone();
      toast.error("Não foi possível usar o microfone. Confira a permissão do navegador.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const descartarGravacao = useCallback(() => {
    // Gravando: interrompe sem guardar (o `onstop` vê que não é mais o ativo).
    const gravador = gravadorAtivo.current;
    gravadorAtivo.current = null;
    if (gravador && gravador.state !== "inactive") gravador.stop();
    microfone.current?.getTracks().forEach((track) => track.stop());
    microfone.current = null;
    setIsRecording(false);
    setUserAudioUrl((antiga) => {
      if (antiga) URL.revokeObjectURL(antiga);
      return null;
    });
  }, []);

  // Card novo: silêncio, gravação do card anterior fora, relógio zerado. E o
  // card seguinte já vai sendo baixado, para a troca não esperar a rede.
  const idDoCard = currentCard?.id;
  useEffect(() => {
    pararAudio();
    descartarGravacao();
    inicioDoCard.current = Date.now();

    const seguinte = cards[currentCardIndex + 1];
    const imagem = urlDaMidia(seguinte?.imagem_principal);
    if (imagem) new Image().src = imagem;
    const audio = urlDaMidia(seguinte?.audio_path);
    if (audio) {
      const pre = new Audio();
      pre.preload = "auto";
      pre.src = audio;
    }
    // `cards` muda quando um card "impossível" volta para o fim da fila; o que
    // interessa aqui é a troca de card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDoCard, currentCardIndex]);

  // Áudio automático: opção do aluno, desligada por padrão. Se o navegador
  // recusar tocar sem um toque, fica em silêncio — o botão continua ali.
  useEffect(() => {
    if (!audioAutomatico || !urlDoAudio || currentStep !== 1 || loading || fim) return;
    tocar(urlDoAudio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDoCard, currentCardIndex, audioAutomatico, loading, fim]);

  // Saiu da tela: nada continua tocando nem gravando.
  useEffect(
    () => () => {
      audioAtual.current?.pause();
      const gravador = gravadorAtivo.current;
      gravadorAtivo.current = null;
      if (gravador && gravador.state !== "inactive") gravador.stop();
      microfone.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  const alternarAudioAutomatico = (ligado: boolean) => {
    setAudioAutomatico(ligado);
    try {
      localStorage.setItem(CHAVE_AUDIO_AUTOMATICO, ligado ? "1" : "0");
    } catch {
      /* sem armazenamento: vale só nesta visita */
    }
  };

  /* --------------------------------------------------------- avaliação */

  const handleDifficulty = async (difficulty: Dificuldade) => {
    if (!currentCard || travaDeResposta.current) return;
    travaDeResposta.current = true;
    setSalvando(difficulty);
    try {
      if (!previewMode) {
        const segundos = Math.min(3600, Math.max(0, Math.round((Date.now() - inicioDoCard.current) / 1000)));
        const gravada = await respostasAPI.create({
          card_id: currentCard.id,
          dificuldade: difficulty,
          tempo_resposta: segundos,
        });
        if (typeof gravada.intervalo === "number") proximasRevisoes.current.set(currentCard.id, gravada.intervalo);
      }

      setResumo((r) => ({ ...r, [difficulty]: r[difficulty] + 1 }));

      let updatedCards = [...cards];
      if (difficulty === "impossivel") {
        updatedCards.push(currentCard);
        setCards(updatedCards);
        toast.info("Este card volta no fim da sessão para você rever.");
      }

      if (currentCardIndex < updatedCards.length - 1) {
        setCurrentCardIndex(currentCardIndex + 1);
        setCurrentStep(1); // Volta para o início para próximo card
      } else if (estudandoPorCategoria && seqIndex < sequenceIds.length - 1) {
        // Estudo por categoria: não avança módulo. Ainda há categorias na
        // sequência: pausa e pergunta.
        setIsPauseModalOpen(true);
      } else {
        // Fim da sessão: o resumo fica na tela, em vez de um aviso que some.
        pararAudio();
        setFim(true);
        if (!estudandoPorCategoria && !previewMode) checkNextModuleProgression();
      }
    } catch (error) {
      console.error("Erro ao registrar dificuldade:", error);
      toast.error(
        sessaoVencida(error)
          ? "Sua sessão expirou. Entre de novo para continuar — esta resposta não foi gravada."
          : "Não foi possível gravar a resposta. Confira sua internet e toque de novo.",
      );
    } finally {
      travaDeResposta.current = false;
      setSalvando(null);
    }
  };

  const checkNextModuleProgression = async () => {
    try {
      if (!currentUser) return;

      const profileData = await alunosAPI.getByUsuarioId(currentUser.id);
      if (!profileData.has_profile || !profileData.aluno) return;

      const alunoId = profileData.aluno.id;
      const currentModuloName = profileData.aluno.modulo_inicial_nome || "";
      const currentModuloCategoriaId = profileData.aluno.modulo_inicial_categoria_id ?? null;

      const stats = await alunosAPI.getEstatisticasAvancadas();
      if (!stats.success || !stats.modulos) return;

      // Encontrar módulo atual nas estatísticas (nome + categoria, pois
      // módulos de categorias diferentes podem ter o mesmo nome)
      const matchesCurrentModulo = (m: any) =>
        m.modulo_nome === currentModuloName &&
        (currentModuloCategoriaId == null || m.categoria_id === currentModuloCategoriaId);
      const currentModuloData = stats.modulos.find(matchesCurrentModulo);

      // Se ele concluiu o módulo (ou seja, cards_vistos >= total_cards)
      if (currentModuloData && currentModuloData.cards_vistos >= currentModuloData.total_cards) {
        // Encontrar o próximo módulo
        const currentIdx = stats.modulos.findIndex(matchesCurrentModulo);
        if (currentIdx !== -1 && currentIdx < stats.modulos.length - 1) {
          const nextModulo = stats.modulos[currentIdx + 1];
          // Mostrar o Modal para avançar para o próximo módulo
          setNextModuloToUnlock(nextModulo);
          setAlunoIdToUpdate(alunoId);
          setProgressionStep("welcome");
          setIsProgressionModalOpen(true);
        }
      }
      // Sem próximo módulo a liberar: fica o resumo da sessão.
    } catch (error) {
      console.error("Erro ao verificar progresso de módulo:", error);
    }
  };

  /* ---------------------------------------------------------- teclado */

  // No computador: Espaço ou Enter mostram a resposta, 1 a 4 avaliam, A toca
  // o áudio, ← volta para a frase. Guardado num ref para o ouvinte ser
  // registrado uma vez só e enxergar sempre o estado mais novo.
  const atalhos = useRef<(e: KeyboardEvent) => void>(() => {});
  atalhos.current = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
    if (loading || fim || erroDeCarga || !currentCard || isPauseModalOpen || isProgressionModalOpen) return;
    // O alvo pode não ser um elemento (a janela, o documento): só elemento tem `closest`.
    const alvo = e.target instanceof HTMLElement ? e.target : null;
    if (alvo && (alvo.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName))) return;
    // Enter e Espaço num botão focado já têm dono: o próprio botão.
    const emBotao = Boolean(alvo?.closest("button, a, [role=switch]"));

    const tecla = e.key.toLowerCase();
    if (tecla === "a") {
      e.preventDefault();
      playNativeAudio();
      return;
    }
    if (currentStep === 1) {
      if ((e.key === " " || e.key === "Enter") && !emBotao) {
        e.preventDefault();
        setCurrentStep(2);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setCurrentStep(2);
      }
      return;
    }
    if (e.key === "ArrowLeft" || e.key === "Backspace") {
      e.preventDefault();
      setCurrentStep(1);
      return;
    }
    const avaliacao = AVALIACOES.find((a) => a.tecla === e.key);
    if (avaliacao) {
      e.preventDefault();
      void handleDifficulty(avaliacao.valor);
    }
  };
  useEffect(() => {
    const ouvir = (e: KeyboardEvent) => atalhos.current(e);
    window.addEventListener("keydown", ouvir);
    return () => window.removeEventListener("keydown", ouvir);
  }, []);

  /* ------------------------------------------------------------- telas */

  const layout = (conteudo: ReactNode) => (
    <MainLayout userType="aluno" userName={user.nome} userPhoto={user.foto} userSexo={user.sexo}>
      {conteudo}
    </MainLayout>
  );

  if (loading) {
    return layout(
      <div className="flex flex-col justify-center items-center h-64 gap-4" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">Preparando seus cards…</p>
      </div>,
    );
  }

  if (erroDeCarga) {
    return layout(
      <div className="text-center py-12 px-4 max-w-md mx-auto" data-testid="erro-de-carga">
        <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <WifiOff className="w-8 h-8 text-destructive" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">
          {erroDeCarga === "sessao" ? "Sua sessão expirou" : "Não foi possível carregar seus cards"}
        </h2>
        <p className="text-muted-foreground mb-8">
          {erroDeCarga === "sessao"
            ? "Entre de novo para continuar de onde parou. Suas respostas já gravadas estão guardadas."
            : "Parece um problema de conexão. Seus cards e seu progresso continuam guardados — é só tentar de novo."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {erroDeCarga === "sessao" ? (
            // Recarrega na raiz do app: é a entrada que decide para onde mandar
            // (o login daqui, ou o da plataforma quando o login é único).
            <Button onClick={() => window.location.assign(import.meta.env.BASE_URL)} size="lg">
              Entrar de novo
            </Button>
          ) : (
            <Button onClick={loadData} size="lg">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar de novo
            </Button>
          )}
          <Button onClick={() => navigate("/aluno")} size="lg" variant="outline">
            Voltar ao painel
          </Button>
        </div>
      </div>,
    );
  }

  if (cards.length === 0) {
    return layout(
      <div className="text-center py-12 px-4">
        <div className="text-6xl mb-6">🎉</div>
        <h2 className="text-3xl font-bold text-foreground mb-4">
          {estudandoPorCategoria ? "Nenhum card nesta categoria" : "Revisões em dia!"}
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          {estudandoPorCategoria
            ? `A categoria ${currentCategoriaNome} ainda não possui cards para estudar.`
            : "Você não possui cards novos ou vencidos no seu módulo atual. Volte amanhã para continuar seu aprendizado."}
        </p>
        <Button onClick={() => navigate("/aluno")} size="lg" className="shadow-lg">
          Voltar ao Dashboard
        </Button>
      </div>,
    );
  }

  const totalRespondido = resumo.facil + resumo.medio + resumo.dificil + resumo.impossivel;

  if (fim) {
    const minutos = Math.max(1, Math.round((Date.now() - inicioDaSessao.current) / 60000));
    // Quando os cards desta sessão voltam, pela resposta mais recente de cada um.
    const quando = { amanha: 0, semana: 0, depois: 0 };
    for (const dias of proximasRevisoes.current.values()) {
      if (dias <= 1) quando.amanha++;
      else if (dias <= 7) quando.semana++;
      else quando.depois++;
    }
    const temAgenda = quando.amanha + quando.semana + quando.depois > 0;

    return layout(
      <div className="max-w-xl mx-auto py-8 px-4 space-y-6 animate-fade-in" data-testid="resumo-da-sessao">
        <div className="text-center space-y-2">
          <div className="text-6xl">🎉</div>
          <h2 className="text-3xl font-bold text-foreground">
            {estudandoPorCategoria ? "Estudo concluído!" : "Revisões de hoje concluídas!"}
          </h2>
          <p className="text-muted-foreground">
            {totalRespondido === 1 ? "1 resposta" : `${totalRespondido} respostas`} em{" "}
            {minutos === 1 ? "1 minuto" : `${minutos} minutos`}
            {previewMode ? " (demonstração: nada foi gravado)" : ""}.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {AVALIACOES.map((a) => (
            <div key={a.valor} className="rounded-xl border border-border bg-card p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-foreground tabular-nums">{resumo[a.valor]}</div>
              <div className="text-xs text-muted-foreground">
                {a.emoji} {a.rotulo}
              </div>
            </div>
          ))}
        </div>

        {temAgenda ? (
          <Card className="bg-muted/50 border-none">
            <CardContent className="p-4 space-y-1">
              <h3 className="font-medium text-foreground">Quando estes cards voltam</h3>
              <p className="text-sm text-muted-foreground">
                {[
                  quando.amanha ? `${quando.amanha} até amanhã` : "",
                  quando.semana ? `${quando.semana} nos próximos 7 dias` : "",
                  quando.depois ? `${quando.depois} mais adiante` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
                . Quanto mais fácil você achou, mais tempo o card espera.
              </p>
            </CardContent>
          </Card>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/aluno")} size="lg" className="shadow-lg">
            Voltar ao painel
          </Button>
          {estudandoPorCategoria ? (
            <Button
              onClick={() => {
                setResumo(RESUMO_VAZIO);
                proximasRevisoes.current.clear();
                inicioDaSessao.current = Date.now();
                if (seqIndex === 0) loadData();
                else setSeqIndex(0);
              }}
              size="lg"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Estudar de novo
            </Button>
          ) : null}
        </div>

        {renderProgressionModal()}
      </div>,
    );
  }

  const posicao = currentCardIndex + 1;
  const faltam = cards.length - posicao;
  const paraRever = Math.max(0, cards.length - Math.max(originalCount, posicao));
  const naFrente = currentStep === 1;

  /* Os controles de pronúncia aparecem nas duas faces do card. */
  const controlesDePronuncia = (
    <div className="p-3 sm:p-4 flex flex-col items-center justify-center gap-4 bg-muted/30 border-b border-border">
      <div className="flex min-w-0 flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full max-w-md px-0 sm:px-4">
        {urlDoAudio ? (
          <Button
            onClick={playNativeAudio}
            size="lg"
            variant="outline"
            aria-pressed={tocando}
            className="min-w-0 h-auto min-h-11 whitespace-normal px-3 py-3 leading-tight shadow-md hover:bg-primary/5 w-full sm:w-auto sm:px-8"
          >
            {tocando ? (
              <Square className="w-5 h-5 mr-2 text-primary fill-primary/20" />
            ) : (
              <Play className="w-5 h-5 mr-2 text-primary fill-primary/10" />
            )}
            {tocando ? "Parar" : "Ouvir Pronúncia"}
          </Button>
        ) : null}
        {podeGravar ? (
          <Button
            onClick={toggleRecording}
            size="lg"
            variant={isRecording ? "destructive" : "outline"}
            className={`min-w-0 h-auto min-h-11 whitespace-normal px-3 py-3 leading-tight shadow-md w-full sm:w-auto sm:px-8 ${isRecording ? "animate-pulse" : "hover:bg-primary/5"}`}
          >
            <Mic className={`w-5 h-5 mr-2 ${isRecording ? "" : "text-primary"}`} />
            {isRecording ? "Parar Gravação" : "Gravar Minha Pronúncia"}
          </Button>
        ) : null}
      </div>
      {isRecording ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          Gravando… fale agora e toque em "Parar Gravação".
        </p>
      ) : null}
      {userAudioUrl && !isRecording && (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 justify-center">
          <Button
            onClick={() => tocar(userAudioUrl)}
            variant="ghost"
            size="sm"
            className="min-w-0 h-auto min-h-10 whitespace-normal text-muted-foreground hover:text-primary"
          >
            <Play className="w-4 h-4 mr-2" /> Ouvir minha gravação
          </Button>
          <Button
            onClick={descartarGravacao}
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 px-3 min-h-10"
            aria-label="Remover gravação"
            title="Remover gravação"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );

  // A face escondida do card não recebe foco: sem isto, o Tab do teclado
  // passava pelos botões do verso com a frente na tela.
  const escondida = { inert: "" } as Record<string, string>;

  function renderProgressionModal() {
    if (!nextModuloToUnlock) return null;
    const avancar = async () => {
      try {
        if (alunoIdToUpdate) {
          await alunosAPI.update(alunoIdToUpdate, {
            modulo_inicial_nome: nextModuloToUnlock.modulo_nome,
            modulo_inicial_categoria_id: nextModuloToUnlock.categoria_id,
          });
          toast.success(`Módulo alterado para ${nextModuloToUnlock.modulo_nome}!`);
          setIsProgressionModalOpen(false);
          window.location.reload();
        }
      } catch (error) {
        console.error("Erro ao avançar de módulo:", error);
        toast.error("Não foi possível liberar o módulo agora. Tente de novo pelo painel.");
        setIsProgressionModalOpen(false);
      }
    };
    return (
      <AlertDialog open={isProgressionModalOpen} onOpenChange={setIsProgressionModalOpen}>
        <AlertDialogContent className="max-w-md border-none shadow-2xl bg-card">
          {progressionStep === "welcome" && (
            <>
              <AlertDialogHeader className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary text-2xl animate-bounce">
                  🎉
                </div>
                <AlertDialogTitle className="text-center text-2xl font-bold text-foreground">
                  Módulo Concluído!
                </AlertDialogTitle>
                <AlertDialogDescription className="text-center text-muted-foreground text-base">
                  Você estudou todas as cartas do <strong>{currentCard?.modulo || "Módulo Atual"}</strong>!
                  <br />
                  Deseja avançar para o próximo módulo: <strong className="text-foreground">{nextModuloToUnlock.modulo_nome}</strong>?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4">
                <AlertDialogCancel onClick={() => setIsProgressionModalOpen(false)} className="w-full sm:w-auto">
                  Ficar Aqui
                </AlertDialogCancel>
                <Button
                  onClick={() => setProgressionStep("ask_video")}
                  className="w-full sm:w-auto bg-primary text-white hover:bg-primary/90"
                >
                  Avançar Módulo
                </Button>
              </AlertDialogFooter>
            </>
          )}

          {progressionStep === "ask_video" && (
            <>
              <AlertDialogHeader className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary text-2xl">
                  📺
                </div>
                <AlertDialogTitle className="text-center text-2xl font-bold text-foreground">
                  Aula de Vocabulário
                </AlertDialogTitle>
                <AlertDialogDescription className="text-center text-muted-foreground text-base">
                  Você assistiu à aula de vocabulário do módulo <strong>{nextModuloToUnlock.modulo_nome}</strong>?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4 justify-center">
                <Button variant="outline" onClick={() => setProgressionStep("confirm_without_video")} className="w-full sm:w-auto">
                  Não
                </Button>
                <Button onClick={avancar} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white">
                  Sim, assisti!
                </Button>
              </AlertDialogFooter>
            </>
          )}

          {progressionStep === "confirm_without_video" && (
            <>
              <AlertDialogHeader className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500 text-2xl animate-pulse">
                  ⚠️
                </div>
                <AlertDialogTitle className="text-center text-xl font-bold text-foreground">
                  Tem certeza?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-center text-muted-foreground text-base">
                  Tem certeza de que quer liberar o módulo <strong>{nextModuloToUnlock.modulo_nome}</strong> sem assistir à aula de vocabulário primeiro?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4 justify-center">
                <Button variant="outline" onClick={() => setIsProgressionModalOpen(false)} className="w-full sm:w-auto">
                  Não, ver aula primeiro
                </Button>
                <Button onClick={avancar} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white">
                  Sim, liberar módulo
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return layout(
      <div className="max-w-5xl min-w-0 mx-auto space-y-4 sm:space-y-6 animate-fade-in">
        <div className="text-center space-y-2">
          {estudandoPorCategoria && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
              Estudando categoria: {currentCategoriaNome}
              {sequenceIds.length > 1 && ` (${seqIndex + 1} de ${sequenceIds.length})`}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl leading-tight font-bold text-foreground">
            {naFrente ? "Leia e pratique a pronúncia" : "Confira a tradução e avalie"}
          </h1>
        </div>

        {/* Quanto falta: o que o aluno mais quer saber no meio da sessão. */}
        <div className="space-y-1.5" data-testid="progresso-da-sessao">
          <Progress
            value={(currentCardIndex / cards.length) * 100}
            className="h-2 bg-muted"
            aria-label={`Card ${posicao} de ${cards.length}`}
          />
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">
              Card {posicao} de {cards.length}
              {paraRever > 0 ? ` · ${paraRever} para rever` : ""}
            </span>
            <span className="tabular-nums">{faltam === 0 ? "último card" : `faltam ${faltam}`}</span>
          </div>
        </div>

        {/* `key` por card: o card novo nasce de frente, sem animação. Sem isto as
            duas faces trocavam de conteúdo enquanto o card ainda desvirava, e a
            TRADUÇÃO do próximo card aparecia por meio segundo antes da frase. */}
        <div className="card-flip-container min-w-0" key={`${currentCardIndex}-${currentCard.id}`}>
          <div className={`card-flip-inner ${currentStep === 2 ? 'flipped' : ''}`}>

            {/* FRENTE DO CARD (Passo 1 - Imagem, Texto Francês, Alphanat e Pronúncia) */}
            <div className="card-flip-front" {...(naFrente ? {} : escondida)}>
              <Card className="w-full min-w-0 shadow-xl border-none overflow-hidden h-full">
                <CardContent className="p-0 h-full flex flex-col">
                  {/* Card Display Area */}
                  <div className="p-0 sm:p-4 bg-muted/10">
                    <CardTextDisplay
                      textoFrances={currentCard.texto_frances || []}
                      alphanat={currentCard.alphanat || []}
                      traducaoPortugues=""
                      imagemPrincipal={currentCard.imagem_principal}
                      step={2}
                      hideAudio={true}
                    />
                  </div>

                  {controlesDePronuncia}

                  {/* Navigation and Actions */}
                  {/* No pé do card: as duas faces dividem a mesma altura (a da mais
                      alta), e a ação fica no mesmo lugar ao virar. */}
                  <div className="mt-auto p-4 sm:p-6 bg-card border-t border-border flex flex-col items-center gap-3">
                    <Button
                      onClick={() => setCurrentStep(2)}
                      size="lg"
                      data-testid="ver-resposta"
                      className="bg-primary hover:bg-primary-dark shadow-md text-lg px-8 py-6 w-full max-w-md rounded-xl transition-all hover:scale-[1.02]"
                    >
                      Ver Resposta
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    <p className="hidden md:block text-xs text-muted-foreground">
                      ou aperte <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-sans">Espaço</kbd>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* VERSO DO CARD (Passo 2 - Resposta e Pronúncia) */}
            <div className="card-flip-back" {...(naFrente ? escondida : {})}>
              <Card className="w-full min-w-0 shadow-xl border-none overflow-hidden h-full">
                <CardContent className="p-0 h-full flex flex-col">
                  {/* Card Display Area */}
                  <div className="p-0 sm:p-4 bg-muted/10">
                    <CardTextDisplay
                      textoFrances={[]}
                      alphanat={[]}
                      traducaoPortugues={currentCard.traducao_portugues || ''}
                      textoTraduzido={currentCard.texto_traduzido}
                      step={3}
                      hideAudio={true}
                    />
                  </div>

                  {controlesDePronuncia}

                  {/* Difficulty Buttons */}
                  <div className="mt-auto p-4 sm:p-6 bg-card space-y-4">
                    <div className="space-y-3">
                      <p className="text-center text-sm font-medium text-muted-foreground">
                        Como foi lembrar deste card?
                      </p>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                        {AVALIACOES.map((a) => (
                          <Button
                            key={a.valor}
                            onClick={() => handleDifficulty(a.valor)}
                            disabled={salvando !== null}
                            data-testid={`avaliar-${a.valor}`}
                            className={`${a.classe} text-white h-14 px-2 text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-60`}
                          >
                            {salvando === a.valor ? (
                              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            ) : (
                              <span className="mr-1.5">{a.emoji}</span>
                            )}
                            {a.rotulo}
                            <kbd className="hidden lg:inline ml-2 rounded bg-black/15 px-1.5 text-xs font-sans">{a.tecla}</kbd>
                          </Button>
                        ))}
                      </div>
                      <p className="text-center text-xs text-muted-foreground">
                        Sua avaliação decide quando o card volta: fácil espera mais, impossível volta ainda nesta sessão.
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <Button
                        onClick={() => setCurrentStep(1)}
                        variant="ghost"
                        className="min-h-11 text-muted-foreground hover:text-primary"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Ver a frase de novo
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>

        {/* Áudio automático: escolha do aluno, guardada no aparelho. */}
        {cards.some((c) => c.audio_path) ? (
          <label className="flex items-center justify-center gap-3 text-sm text-muted-foreground cursor-pointer">
            <Switch
              checked={audioAutomatico}
              onCheckedChange={alternarAudioAutomatico}
              aria-label="Tocar a pronúncia sozinha ao abrir cada card"
            />
            Tocar a pronúncia sozinha ao abrir cada card
          </label>
        ) : null}

        {/* Instructions Card */}
        <Card className="bg-muted/50 border-none">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Play className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-foreground mb-1">Como funciona?</h3>
                <p className="text-sm text-muted-foreground">
                  <strong>1.</strong> Leia a frase em francês, ouça e repita em voz alta.{" "}
                  <strong>2.</strong> Toque em "Ver Resposta" para conferir a tradução e diga como foi — é isso que decide quando o card volta.
                </p>
                <p className="hidden md:block text-xs text-muted-foreground mt-2">
                  No computador: <kbd className="rounded border border-border bg-background px-1 font-sans">Espaço</kbd> mostra a
                  resposta, <kbd className="rounded border border-border bg-background px-1 font-sans">1</kbd> a{" "}
                  <kbd className="rounded border border-border bg-background px-1 font-sans">4</kbd> avaliam,{" "}
                  <kbd className="rounded border border-border bg-background px-1 font-sans">A</kbd> toca o áudio.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Modal de Pausa entre categorias da sequência */}
        <AlertDialog open={isPauseModalOpen} onOpenChange={setIsPauseModalOpen}>
          <AlertDialogContent className="max-w-md border-none shadow-2xl bg-card">
            <AlertDialogHeader className="space-y-3">
              <div className="mx-auto w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-500 text-2xl">
                ✅
              </div>
              <AlertDialogTitle className="text-center text-2xl font-bold text-foreground">
                Categoria concluída!
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center text-muted-foreground text-base">
                Você concluiu <strong>{currentCategoriaNome}</strong>.
                <br />
                Deseja seguir para a próxima categoria: <strong className="text-foreground">{nextCategoriaNome}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex sm:flex-row gap-2 mt-4 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPauseModalOpen(false);
                  setFim(true);
                }}
                className="w-full sm:w-auto"
              >
                Encerrar por aqui
              </Button>
              <Button
                onClick={() => {
                  setIsPauseModalOpen(false);
                  setSeqIndex((i) => i + 1);
                }}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white"
              >
                Sim, continuar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {renderProgressionModal()}
      </div>,
  );
};

export default AlunoCards;
